import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import { leverageApi } from '../api';
import type { LeveragePayoutItem, TranslateFn } from '../types';
import { formatUsdc, getErrorMessage } from '../utils';

const I18N_PREFIX = 'module.boldPredictionCampaign';

const tL = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

const DEFAULT_LIMIT = 50;

type RewardPayoutHistoryDialogProps = {
  open: boolean;
  captainId: string;
  captainWallet?: string;
  onClose: () => void;
};

export default function RewardPayoutHistoryDialog({
  open,
  captainId,
  captainWallet,
  onClose,
}: RewardPayoutHistoryDialogProps) {
  const t = useTranslate();
  const reqIdRef = useRef(0);

  const [items, setItems] = useState<LeveragePayoutItem[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !captainId) return;
    reqIdRef.current += 1;
    const reqId = reqIdRef.current;
    setLoading(true);
    setError(null);
    leverageApi
      .listPayouts({ search: captainId, limit, offset })
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setError(getErrorMessage(err));
      })
      .finally(() => {
        if (reqId !== reqIdRef.current) return;
        setLoading(false);
      });
  }, [open, captainId, limit, offset]);

  useEffect(() => {
    if (!open) {
      setItems([]);
      setTotal(0);
      setOffset(0);
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6">{tL(t, 'stats.rewardHistory.title')}</Typography>
          {captainId || captainWallet ? (
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
              <Typography variant="body2" fontWeight={600}>
                {tL(t, 'stats.rewardHistory.col.teamOwner')}:
              </Typography>
              {captainId ? (
                <Typography variant="body2" fontWeight={600}>
                  {captainId}
                </Typography>
              ) : null}
              {captainWallet ? (
                <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                  ({captainWallet})
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tL(t, 'stats.rewardHistory.col.triggerTime')}</TableCell>
                <TableCell>{tL(t, 'stats.rewardHistory.col.userId')}</TableCell>
                <TableCell align="center">{tL(t, 'stats.rewardHistory.col.tier')}</TableCell>
                <TableCell align="right">{tL(t, 'stats.rewardHistory.col.reward')}</TableCell>
                <TableCell>{tL(t, 'stats.rewardHistory.col.txHash')}</TableCell>
                <TableCell align="center">{tL(t, 'stats.rewardHistory.col.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Stack alignItems="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : items.length ? (
                items.map((row) => <PayoutRow key={row.id} row={row} t={t} />)
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Stack alignItems="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {tL(t, 'stats.empty')}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={Math.floor(offset / limit)}
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100, 200]}
          onPageChange={(_, page) => setOffset(page * limit)}
          onRowsPerPageChange={(e) => {
            setLimit(Number(e.target.value));
            setOffset(0);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ra.action.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

const PayoutRow = memo(({ row, t }: { row: LeveragePayoutItem; t: TranslateFn }) => (
  <TableRow hover>
    <TableCell>
      <Typography variant="caption" color="text.secondary">
        {row.triggerTime ? dayjs(row.triggerTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body2">{row.userId}</Typography>
    </TableCell>
    <TableCell align="center">
      <Chip label={`T${row.tier}`} color="success" size="small" />
    </TableCell>
    <TableCell align="right">
      <Typography variant="body2" fontWeight={600}>
        {formatUsdc(row.reward)} USDC
      </Typography>
    </TableCell>
    <TableCell>
      {row.txHash ? (
        <Tooltip title={row.txHash} placement="top">
          <Link
            href={`https://polygonscan.com/tx/${row.txHash}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
            }}
          >
            {shortenHash(row.txHash)}
          </Link>
        </Tooltip>
      ) : (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      )}
    </TableCell>
    <TableCell align="center">
      {row.errorCode ? (
        <Tooltip
          title={
            <span style={{ textTransform: 'capitalize' }}>
              {row.errorCode.replace(/_/g, ' ').toLowerCase()}
            </span>
          }
          placement="top"
          arrow
        >
          <Chip
            label={tL(t, `stats.rewardHistory.status.${row.status || 'unknown'}`)}
            color={statusColor(row.status)}
            size="small"
          />
        </Tooltip>
      ) : (
        <Chip
          label={tL(t, `stats.rewardHistory.status.${row.status || 'unknown'}`)}
          color={statusColor(row.status)}
          size="small"
        />
      )}
    </TableCell>
  </TableRow>
));

PayoutRow.displayName = 'PayoutRow';

function shortenHash(hash: string, head = 5, tail = 5): string {
  if (!hash) return '';
  if (hash.length <= head + tail) return hash;
  return `${hash.slice(0, head)}...${hash.slice(-tail)}`;
}

function statusColor(
  status: string
): 'success' | 'warning' | 'info' | 'error' | 'default' {
  switch (status) {
    case 'sent':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}
